import { useState } from 'react'
import { compose } from 'recompose'

import Button from '@components/Button'
import featureToggles from 'shared/featureToggles'
import { loadOnMount } from 'shared/hoc'

import useRequestPending from 'shared/hooks/useRequestPending'
import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'

import PromoPrice from './PromoPrice'
import { ProductWithRates } from '../ProductWithRates'
import { getLocationRates } from 'subscription-flow/canada/actions'
import {
    productsSelector,
    hasPromoPriceSelector,
    subscriptionLocationRatesPendingSelector,
} from 'subscription-flow/canada/selectors'

import purchaseStyles from 'subscription-flow/styles.module.scss'

const ProductsList = ({
    value,
    onNext,
    startDate,
    hasPromoPrice,
    products,
    setItemAddingToWaitList,
    pendingLocationRates,
    getLocationRates,
    waitingListIdProduct,
    isWaitListFlow,
}) => {
    const [selectedRate, setSelectedRate] = useState(value || null)
    const [promoCode, setPromoCode] = useState('')

    const { error, isLoading } = useRequestPending(pendingLocationRates)
    const waitlistProducts = products.filter(item => item.prdctCd === waitingListIdProduct)
    const productsForRender = isWaitListFlow && waitingListIdProduct ? waitlistProducts : products
    const showNextButton = isWaitListFlow ? waitlistProducts : !selectedRate?.isWaitList

    const selectRate = (rate, isWaitList = false) => {
        if (selectedRate?.ratePlanId !== rate.ratePlanId) {
            const quantity = selectedRate?.prdctCd === rate.prdctCd ? selectedRate?.quantity : 1
            setSelectedRate({
                prdctCd: rate.prdctCd,
                rateId: rate.ratePlanId,
                quantity,
                description: rate.description,
                isWaitList,
            })
        }
    }

    const changeQuantity = (productCode, quantity) => {
        if (selectedRate?.prdctCd === productCode && quantity >= 1 && quantity <= 20) {
            setSelectedRate(prevState => ({ ...prevState, quantity }))
        }
    }

    const onNextClick = () => {
        const product = products.find(prd => prd.prdctCd === selectedRate.prdctCd)
        onNext({ ...selectedRate, displayValue: product.prdctName })
    }

    const applyPromoCode = reset => {
        getLocationRates(startDate, reset ? '' : promoCode)
    }

    const addToWaitList = (rateId, waitListId) => {
        setItemAddingToWaitList({ rateId, waitListId, startDate })
    }

    if (error) {
        return <ErrorMessage messages={[error]} />
    }

    return (
        <CardSection className={purchaseStyles.contentSection} loading={isLoading}>
            {!isWaitListFlow && (
                <PromoPrice
                    hasPromoPrice={hasPromoPrice}
                    applyPromoCode={applyPromoCode}
                    onPromoCodeChange={setPromoCode}
                    promoCode={promoCode}
                    resetSelectedRate={setSelectedRate}
                    selectedRate={selectedRate}
                    products={products}
                />
            )}
            {productsForRender &&
                productsForRender.map((prd, i) => (
                    <ProductWithRates
                        key={i}
                        changeQuantity={
                            !featureToggles.hasSubcriptionLite &&
                            selectedRate?.prdctCd === prd.prdctCd &&
                            changeQuantity
                        }
                        selectedRate={selectedRate}
                        selectRate={selectRate}
                        addToWaitList={addToWaitList}
                        prd={prd}
                        waitingListIdProduct={isWaitListFlow && waitlistProducts && waitingListIdProduct}
                    />
                ))}
            {showNextButton && (
                <div className="d-flex justify-content-end mt-6">
                    <Button btnStyle="primary" onClick={onNextClick} disabled={!selectedRate}>
                        <T>subscription.purchase.label.next</T>
                    </Button>
                </div>
            )}
        </CardSection>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            products: productsSelector,
            hasPromoPrice: hasPromoPriceSelector,
            pendingLocationRates: subscriptionLocationRatesPendingSelector,
        }),
        { getLocationRates }
    ),
    loadOnMount('getLocationRates', ({ startDate }) => [startDate])
)(ProductsList)
